import { Swiper } from '@services';

class ImageSwiper extends HTMLElement {
  /** @type {import('swiper/types').SwiperOptions} */ #config = { slidesPerView: 1, lazy: true };
  /** @type {import('swiper').Swiper | undefined} */ swiper;
  /** @type {Map<number, Element[]>} */ #controllersMap = new Map();
  /** @type {Element[]} */ #slides = [...this.querySelectorAll('.swiper-slide')];

  static ACTIVE_CLASS = 'active';

  async connectedCallback() {
    this.#setControllers();
    this.#prepareSlides();
    await this.#initSwiper();
    this.#registerEvents();
  }

  /**
   * Builds `#controllersMap` by querying all menus that reference this swiper
   * via `data-swiper-id` and mapping each button to corresponding slide index.
   * @returns {void}
   */
  #setControllers() {
    const menus = [...document.querySelectorAll(`[data-swiper-id="${this.id}"]`)]?.map((menu) => [
      ...menu.querySelectorAll('button'),
    ]);
    if (!menus.length) return;

    menus.forEach((menu) => {
      menu.forEach((button, index) => {
        const value = this.#controllersMap.get(index) || [];
        value.push(button);
        this.#controllersMap.set(index, value);
      });
    });
  }

  /**
   * Injects a lazy preloader <div> for images with `loading="lazy"`.
   * @returns {void}
   */
  #prepareSlides() {
    this.#slides.forEach((slide) => {
      if (slide.querySelector('img')?.getAttribute('loading') === 'lazy') {
        const preloader = document.createElement('div');
        preloader.classList.add('swiper-lazy-preloader');
        slide.appendChild(preloader);
      }
    });
  }

  /**
   * Initializes the Swiper instance on this element.
   * @returns {Promise<void>}
   */
  async #initSwiper() {
    try {
      this.swiper = await Swiper(this, this.#config);
    } catch (error) {
      console.error('Error initializing Swiper:', error);
    }
  }

  /**
   * Registers Swiper and controller button event listeners.
   * @returns {void}
   */
  #registerEvents() {
    this.swiper.on('slideChange', this.#onSlideChange.bind(this));

    for (const [index, buttons] of this.#controllersMap.entries()) {
      buttons.forEach((button) => {
        button.addEventListener('click', this.#onControllerClick.bind(this, index));
      });
    }
  }

  /**
   * Fires when the slide changes.
   * @returns {void}
   */
  #onSlideChange() {
    this.#toggleControllers();
  }

  /**
   * Toggles the active class on all controller buttons to reflect the current slide index.
   * @returns {void}
   */
  #toggleControllers() {
    const { activeIndex } = this.swiper;

    for (const [index, buttons] of this.#controllersMap.entries()) {
      buttons.forEach((button) => {
        button.classList.toggle(ImageSwiper.ACTIVE_CLASS, index === activeIndex);
      });
    }
  }

  /**
   * Slides to the given index when a controller button is clicked.
   * @param {number} index
   * @returns {void}
   */
  #onControllerClick(index) {
    if (index === this.swiper.activeIndex) return;
    this.swiper.slideTo(index);
  }
}

if (!customElements.get('image-swiper')) {
  customElements.define('image-swiper', ImageSwiper);
}
